import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Instagram, MessageCircle, Navigation } from 'lucide-react';
import SectionHead from '@/components/SectionHead';
import { CONTACT } from '@/data/content';
import { fadeUp, viewportOnce } from '@/lib/motion';

const btnBase = 'flex items-center justify-center gap-2.5 font-display tracking-widest text-lg sm:text-xl px-6 py-4 rounded-sm transition-colors duration-300';

export default function Contact() {
  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-night-surface border-t border-white/5" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHead
          chapter="15"
          eyebrow="Contact"
          title={"LET'S TALK\nSOCCER."}
          description="Questions about programs, evaluations or tryouts — the fastest way to reach us is WhatsApp."
          testId="contact-heading"
        />

        <div className="grid lg:grid-cols-2 gap-10">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}>
            <div className="space-y-5" data-testid="contact-info">
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-brand-gold mt-1 shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-gray-500 font-semibold">Location</div>
                  <div className="mt-1 text-white font-semibold">{CONTACT.location}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={20} className="text-brand-gold mt-1 shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-gray-500 font-semibold">WhatsApp Hotline</div>
                  <div className="mt-1 text-white font-semibold">{CONTACT.phoneDisplay}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={20} className="text-brand-gold mt-1 shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-gray-500 font-semibold">Official Email</div>
                  <div className="mt-1 text-white font-semibold break-all">{CONTACT.email}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Instagram size={20} className="text-brand-gold mt-1 shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-gray-500 font-semibold">Instagram</div>
                  <div className="mt-1 text-white font-semibold">{CONTACT.instagramHandle}</div>
                </div>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-3" data-testid="contact-buttons">
              <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-button"
                className={`${btnBase} bg-[#25D366] text-night hover:brightness-110 transition-[filter]`}>
                <MessageCircle size={19} /> WHATSAPP
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-button"
                className={`${btnBase} border border-brand-gold/70 text-brand-gold hover:bg-brand-gold hover:text-night`}>
                <Mail size={19} /> EMAIL US
              </a>
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram-button"
                className={`${btnBase} border border-white/25 text-white hover:border-brand-gold hover:text-brand-gold`}>
                <Instagram size={19} /> INSTAGRAM
              </a>
              <a href={CONTACT.mapsLink} target="_blank" rel="noreferrer" data-testid="contact-directions-button"
                className={`${btnBase} border border-white/25 text-white hover:border-brand-gold hover:text-brand-gold`}>
                <Navigation size={19} /> GET DIRECTIONS
              </a>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={viewportOnce}
            className="relative rounded-sm overflow-hidden border border-white/10 gold-hairline min-h-[340px]"
            data-testid="contact-map"
          >
            <iframe
              title="Independence Park, Canton, Michigan"
              src={CONTACT.mapsEmbed}
              className="absolute inset-0 w-full h-full grayscale invert-[0.92] contrast-[0.85]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute bottom-3 left-3 bg-black/80 backdrop-blur-sm border border-brand-gold/40 rounded-sm px-4 py-2 pointer-events-none">
              <span className="font-display text-lg tracking-widest text-brand-gold uppercase">7546 Baron Dr — Home of ASA</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
